import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Book, 
  Users, 
  PieChart, 
  Search, 
  Filter, 
  AlertTriangle, 
  ChevronRight,
  Clock,
  FileText
} from 'lucide-react';
import styled from 'styled-components';
import { PageHeader, PageTitle, BackButton } from './TeacherJourney';
import ResourceGenerator from './ResourceGenerator';

export const ClassDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ClassInfoCard = styled.div`
  background-color: #f3f0ff;
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e9d5ff;
`;

export const ClassInfoHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

export const ClassSubject = styled.h3`
  font-weight: 700;
  color: #581c87;
  margin-left: 0.5rem;
`;

export const ClassStats = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
  color: #6b7280;
`;

export const StatItem = styled.div`
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.25rem;
    color: #7c3aed;
  }
`;

export const SearchContainer = styled.div`
  display: flex;
  margin-bottom: 1rem;
`;

export const SearchInput = styled.div`
  flex: 1;
  position: relative;
  
  input {
    width: 100%;
    padding: 0.5rem 0.5rem 0.5rem 2.5rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    
    &:focus {
      outline: none;
      border-color: #7c3aed;
    }
  }
  
  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
  }
`;

export const FilterButton = styled.button`
  padding: 0.5rem;
  margin-left: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: white;
  color: #6b7280;
  
  &:hover {
    background-color: #f9fafb;
  }
`;

export const StudentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
`;

export const StudentItem = styled.button`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  width: 100%;
  text-align: left;
  
  &:hover {
    background-color: #f9fafb;
  }
`;

export const StudentInfo = styled.div`
  flex: 1;
`;

export const StudentName = styled.div`
  font-weight: 500;
  color: #111827;
`;

export const StudentMeta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

export const ProgressBar = styled.div`
  width: 6rem;
  height: 0.5rem;
  background-color: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
  margin: 0 1rem;
`;

export const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background-color: ${({ progress }) => {
    if (progress >= 80) return '#059669';
    if (progress >= 70) return '#2563eb';
    if (progress >= 60) return '#d97706';
    return '#dc2626';
  }};
  width: ${({ progress }) => `${progress}%`};
  border-radius: 9999px;
`;

export const ActivitiesSection = styled.div`
  margin-top: 1.5rem;
`;

export const SectionTitle = styled.h3`
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.75rem;
`;

export const ActivityCard = styled.div`
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
`;

export const ActivityHeader = styled.div`
  display: flex;
  align-items: start;
  margin-bottom: 0.5rem;
`;

export const ActivityIcon = styled.div<{ variant: 'purple' | 'blue' }>`
  padding: 0.5rem;
  border-radius: 9999px;
  background-color: ${({ variant }) => variant === 'purple' ? '#f3f0ff' : '#eff6ff'};
  margin-right: 0.75rem;
  
  svg {
    color: ${({ variant }) => variant === 'purple' ? '#7c3aed' : '#2563eb'};
  }
`;

export const ActivityContent = styled.div`
  flex: 1;
`;

export const ActivityTitle = styled.h4`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
`;

export const ActivityDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

export const ActivityMeta = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #6b7280;
  
  svg {
    margin-right: 0.25rem;
  }
`;

interface Student {
  _id: string;
  firstName: string;
  lastName: string;
  grade: string;
  email: string;
}

interface Class {
  _id: string;
  name: string;
  grade: string;
  students: Student[];
}

interface ClassDetailsProps {
  classData: Class;
  students: Student[];
  subject: string;
  term: string;
  onBack: () => void;
  onSelectStudent: (student: Student) => void;
}

const Container = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  color: #111827;
  font-size: 1.25rem;
  font-weight: 600;
`;

const StudentList = styled.div`
  padding: 1.5rem;
`;

const StudentCard = styled.div`
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f9fafb;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StudentInfoCard = styled.div`
  flex: 1;
`;

const ClassDetails: React.FC<ClassDetailsProps> = ({
  classData,
  students,
  subject,
  term,
  onBack,
  onSelectStudent,
}) => {
  const [resourceGenStudent, setResourceGenStudent] = useState<Student | null>(null);
  return (
    <Container>
      <Header>
        <Title>{classData.name} - {classData.grade}</Title>
        <BackButton onClick={onBack}>Back to Dashboard</BackButton>
      </Header>
      <StudentList>
        {students.map((student) => (
          <StudentCard key={student._id} onClick={() => onSelectStudent(student)}>
            <StudentInfoCard>
              <StudentName>{student.firstName} {student.lastName}</StudentName>
              <StudentInfo>Email: {student.email}</StudentInfo>
              <StudentInfo>Grade: {student.grade}</StudentInfo>
            </StudentInfoCard>
          </StudentCard>
        ))}
      </StudentList>
    </Container>
  );
};

export default ClassDetails; 